import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import {
  Mail, RefreshCw, AlertTriangle, CheckCircle2, Send, Settings, Eye, CalendarClock,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const PESO = (n) => Number(n || 0).toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });
const ISO_TO_LOCAL = (s) => (s ? new Date(s).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' }) : '—');

// Iter 236.f — badge de seguimiento de respuesta
const ESTADO_RESPUESTA_LABEL = {
  respondio_a_tiempo: { label: '✅ Respondió', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', help: 'Envió estado de cuenta dentro de 7 días del último pedido' },
  respondio_tarde: { label: '🟠 Tardío', cls: 'bg-orange-100 text-orange-800 border-orange-300', help: 'Respondió pero después de los 7 días' },
  esperando: { label: '🟡 Esperando', cls: 'bg-amber-100 text-amber-800 border-amber-300', help: 'Envío reciente — esperando respuesta dentro del plazo de 7 días' },
  sin_respuesta: { label: '🔴 Sin respuesta', cls: 'bg-rose-100 text-rose-800 border-rose-300', help: 'Pasaron más de 7 días desde el último envío y no respondió' },
  nunca_solicitado: { label: 'Nunca solicitado', cls: 'bg-gray-100 text-gray-600 border-gray-300', help: 'No se le envió todavía' },
};

export default function SolicitarEstadosCuenta() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, con_email: 0, sin_email: 0, desync_alegra: 0, por_estado_respuesta: {} });
  const [filter, setFilter] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('rut') || ''; }
    catch { return ''; }
  });
  const [soloConEmail, setSoloConEmail] = useState(() => {
    try { return !new URLSearchParams(window.location.search).get('rut'); }
    catch { return true; }
  });
  const [filtroEstado, setFiltroEstado] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [config, setConfig] = useState({ habilitado: false, frecuencia: 'mensual', dia: 1, hora: 9, firma: 'Concilia (Agente Pago Proveedores)', ultimo_envio_auto: null });
  const [savingConfig, setSavingConfig] = useState(false);
  const [historial, setHistorial] = useState([]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/contabilidad/solicitud-estado-cuenta/proveedores-con-deuda');
      setRows(r.data.items || []);
      setSummary({
        total: r.data.total || 0,
        con_email: r.data.con_email || 0,
        sin_email: r.data.sin_email || 0,
        desync_alegra: r.data.desync_alegra || 0,
        por_estado_respuesta: r.data.por_estado_respuesta || {},
      });
    } catch (e) {
      toast.error('Error cargando proveedores: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const r = await api.get('/contabilidad/solicitud-estado-cuenta/config-auto');
      setConfig({ ...config, ...r.data });
    } catch (e) { /* ignore */ }
    try {
      const h = await api.get('/contabilidad/solicitud-estado-cuenta/historial?limit=10');
      setHistorial(h.data.items || []);
    } catch (e) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadRows(); loadConfig(); }, [loadRows, loadConfig]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const normRutLocal = (s) => String(s || '').replace(/[.\-\s]/g, '').toLowerCase();
    const qn = normRutLocal(q);
    return rows.filter((r) => {
      if (soloConEmail && !(r.email || '').trim()) return false;
      if (filtroEstado && (r.estado_respuesta || 'nunca_solicitado') !== filtroEstado) return false;
      if (!q) return true;
      return (
        (r.nombre || '').toLowerCase().includes(q) ||
        (r.rut || '').toLowerCase().includes(q) ||
        (qn && normRutLocal(r.rut).includes(qn)) ||
        (qn && normRutLocal(r.rut_normalizado).includes(qn)) ||
        (r.email || '').toLowerCase().includes(q)
      );
    });
  }, [rows, filter, soloConEmail, filtroEstado]);

  const idKey = (r) => r.alegra_id || `rut:${r.rut_normalizado}`;

  const toggleOne = (r) => {
    const k = idKey(r);
    const next = new Set(selected);
    if (next.has(k)) next.delete(k); else next.add(k);
    setSelected(next);
  };

  const toggleAllVisible = () => {
    const visibleKeys = filtered.filter((r) => (r.email || '').trim()).map(idKey);
    const allSelected = visibleKeys.every((k) => selected.has(k));
    const next = new Set(selected);
    if (allSelected) visibleKeys.forEach((k) => next.delete(k));
    else visibleKeys.forEach((k) => next.add(k));
    setSelected(next);
  };

  const startEditEmail = (r) => {
    setEditingId(idKey(r));
    setEditingValue(r.email || '');
  };

  const saveEmail = async (r) => {
    const nuevo = (editingValue || '').trim();
    if (nuevo === (r.email || '').trim()) {
      setEditingId(null);
      return;
    }
    if (!nuevo) {
      toast.error('Ingresa un email válido');
      return;
    }
    setSavingEmail(true);
    try {
      // Iter 280 (Carlos): guarda SOLO el email de Estados de Cuenta (CECP).
      // No sincroniza con Alegra ni pisa el email de Compras/Cotizaciones.
      const res = await api.post('/contabilidad/solicitud-estado-cuenta/proveedor-email', {
        alegra_id: r.alegra_id || '',
        rut: r.rut || '',
        nombre: r.nombre || '',
        email: nuevo,
      });
      const fuente = res?.data?.email_fuente;
      toast.success(
        fuente === 'cecp'
          ? 'Email de Estados de Cuenta guardado (el email de Compras no se modificó)'
          : 'Email de Estados de Cuenta guardado (local) — proveedor sin registro en el maestro'
      );
      setEditingId(null);
      await loadRows();
    } catch (e) {
      toast.error('No se pudo guardar: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSavingEmail(false);
    }
  };

  const enviarSeleccionados = async () => {
    const ids = Array.from(selected);
    if (!ids.length) {
      toast.error('Selecciona al menos un proveedor');
      return;
    }
    setSending(true);
    try {
      const r = await api.post('/contabilidad/solicitud-estado-cuenta/enviar', {
        alegra_ids: ids,
        firma: config.firma || 'Concilia (Agente Pago Proveedores)',
      });
      toast.success(`Enviados: ${r.data.enviados} · Fallidos: ${r.data.fallidos}`);
      setSelected(new Set());
      await loadRows();
      await loadConfig();
    } catch (e) {
      toast.error('Error enviando: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSending(false);
    }
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      const r = await api.put('/contabilidad/solicitud-estado-cuenta/config-auto', {
        habilitado: config.habilitado,
        dia: Number(config.dia) || 1,
        hora: Number(config.hora) || 9,
        firma: config.firma,
      });
      setConfig(r.data);
      toast.success('Configuración guardada');
      setShowConfig(false);
    } catch (e) {
      toast.error('Error guardando: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSavingConfig(false);
    }
  };

  const seleccionadosConEmail = Array.from(selected).filter((k) => {
    const r = rows.find((x) => idKey(x) === k);
    return r && (r.email || '').trim();
  }).length;

  return (
    <div className="p-6 space-y-4" data-testid="solicitar-estados-cuenta-page">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-600" />
              Solicitar Estados de Cuenta a Proveedores
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Proveedores con deuda en CxP local o saldo abierto en Alegra. El correo se envía desde
              <strong> concilia@chirinosteel.com</strong> y motiva al proveedor a responder con su estado de cuenta.
              El email que edites aquí es el de <strong>Estados de Cuenta (CECP)</strong> — no modifica el email de Compras/Cotizaciones.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={loadRows} disabled={loading} data-testid="btn-refresh">
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refrescar
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowPreview(true)} data-testid="btn-preview">
              <Eye className="h-4 w-4 mr-1" />
              Ver email
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowConfig(true)} data-testid="btn-config">
              <Settings className="h-4 w-4 mr-1" />
              Auto-envío
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <Card><CardContent className="p-3"><div className="text-xs text-gray-500">Proveedores totales</div><div className="text-2xl font-bold">{summary.total}</div></CardContent></Card>
            <Card><CardContent className="p-3"><div className="text-xs text-emerald-700">Con email</div><div className="text-2xl font-bold text-emerald-700">{summary.con_email}</div></CardContent></Card>
            <Card><CardContent className="p-3"><div className="text-xs text-amber-700">Sin email</div><div className="text-2xl font-bold text-amber-700">{summary.sin_email}</div></CardContent></Card>
            <Card><CardContent className="p-3"><div className="text-xs text-rose-700 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Desync con Alegra</div><div className="text-2xl font-bold text-rose-700">{summary.desync_alegra}</div></CardContent></Card>
          </div>

          {/* KPIs de seguimiento de respuesta (Iter 236.f) */}
          <div className="flex flex-wrap items-center gap-2 mb-4 text-xs">
            <span className="text-gray-500">Seguimiento:</span>
            {Object.entries(ESTADO_RESPUESTA_LABEL).map(([key, def]) => {
              const cnt = summary.por_estado_respuesta?.[key] || 0;
              const isActive = filtroEstado === key;
              return (
                <button
                  key={key}
                  onClick={() => setFiltroEstado(isActive ? '' : key)}
                  className={`px-2 py-1 rounded border ${def.cls} ${isActive ? 'ring-2 ring-offset-1 ring-blue-400' : 'opacity-80 hover:opacity-100'} transition`}
                  title={def.help}
                  data-testid={`filter-estado-${key}`}
                >
                  {def.label} <strong className="ml-1">{cnt}</strong>
                </button>
              );
            })}
            {filtroEstado && (
              <button onClick={() => setFiltroEstado('')} className="text-blue-600 hover:underline ml-1" data-testid="clear-filter-estado">
                limpiar filtro
              </button>
            )}
          </div>

          {summary.desync_alegra > 0 && (
            <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 mb-3 text-sm">
              ⚠️ Hay <strong>{summary.desync_alegra}</strong> proveedores con CxP local pero <strong>sin contacto en Alegra</strong>.
              Alegra debe ser la fuente mandatoria — revisá Contactos para sincronizar.
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 mb-3">
            <Input
              placeholder="Buscar por nombre, RUT o email…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="max-w-md"
              data-testid="input-filter"
            />
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={soloConEmail} onCheckedChange={setSoloConEmail} data-testid="switch-con-email" />
              Sólo con email
            </label>
            <div className="flex-1" />
            <div className="text-sm">
              <Badge variant="outline">
                {seleccionadosConEmail} seleccionados con email
              </Badge>
            </div>
            <Button
              onClick={enviarSeleccionados}
              disabled={sending || seleccionadosConEmail === 0}
              data-testid="btn-enviar"
            >
              <Send className={`h-4 w-4 mr-1 ${sending ? 'animate-pulse' : ''}`} />
              {sending ? 'Enviando…' : `Enviar a ${seleccionadosConEmail}`}
            </Button>
          </div>

          <div className="overflow-x-auto border rounded">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={filtered.length > 0 && filtered.filter((r) => (r.email || '').trim()).every((r) => selected.has(idKey(r)))}
                      onCheckedChange={toggleAllVisible}
                      data-testid="check-all"
                    />
                  </TableHead>
                  <TableHead>Proveedor</TableHead>
                  <TableHead>RUT</TableHead>
                  <TableHead>Email Estados de Cuenta</TableHead>
                  <TableHead className="text-right">Saldo CxP</TableHead>
                  <TableHead className="text-right">Saldo Alegra</TableHead>
                  <TableHead>Últ. solicitud enviada</TableHead>
                  <TableHead>Últ. estado recibido</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 && !loading && (
                  <TableRow><TableCell colSpan={8} className="text-center text-gray-500 py-6">
                    {filter.trim() && rows.length > 0 ? (
                      <div className="space-y-1">
                        <div>No se encontró <strong>&quot;{filter}&quot;</strong> entre los proveedores con deuda registrada en Alegra/CxP.</div>
                        <div className="text-xs text-gray-400">Puede ser un faltante de conciliación sin deuda registrada o sin contacto en Alegra. Créalo en Contactos para poder solicitarle el estado de cuenta.</div>
                        <button onClick={() => setFilter('')} className="text-blue-600 hover:underline text-xs" data-testid="clear-rut-filter">Ver todos los proveedores</button>
                      </div>
                    ) : 'Sin proveedores'}
                  </TableCell></TableRow>
                )}
                {filtered.map((r) => {
                  const k = idKey(r);
                  const isEditing = editingId === k;
                  const noEmail = !(r.email || '').trim();
                  return (
                    <TableRow key={k} className={noEmail ? 'bg-amber-50/40' : ''}>
                      <TableCell>
                        <Checkbox
                          checked={selected.has(k)}
                          onCheckedChange={() => toggleOne(r)}
                          disabled={noEmail}
                          data-testid={`check-${k}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="font-medium flex items-center gap-2 flex-wrap">
                          {r.nombre || '(sin nombre)'}
                          {(() => {
                            const e = r.estado_respuesta || 'nunca_solicitado';
                            const def = ESTADO_RESPUESTA_LABEL[e];
                            if (!def || e === 'nunca_solicitado') return null;
                            return (
                              <span
                                className={`text-xs px-1.5 py-0.5 rounded border ${def.cls}`}
                                title={def.help}
                                data-testid={`badge-estado-${k}`}
                              >
                                {def.label}
                              </span>
                            );
                          })()}
                        </div>
                        {r.fuentes?.includes('sin_alegra_contact') && (
                          <Badge variant="outline" className="text-xs text-rose-700 border-rose-300">⚠ sin Alegra</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-gray-600">{r.rut || '—'}</TableCell>
                      <TableCell>
                        {isEditing ? (
                          <div className="flex items-center gap-1">
                            <Input
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  saveEmail(r);
                                } else if (e.key === 'Escape') {
                                  e.preventDefault();
                                  setEditingId(null);
                                }
                              }}
                              className="h-8 text-sm"
                              data-testid={`input-email-${k}`}
                              placeholder="email@proveedor.cl  (Enter para guardar)"
                              autoFocus
                            />
                            <Button
                              size="sm"
                              onClick={() => saveEmail(r)}
                              disabled={savingEmail}
                              className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white px-2 gap-1"
                              data-testid={`btn-save-${k}`}
                              title="Guardar (Enter)"
                            >
                              <CheckCircle2 className="h-3.5 w-3.5" />
                              <span className="text-xs">{savingEmail ? 'Guardando…' : 'Guardar'}</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8"
                              onClick={() => setEditingId(null)}
                              title="Cancelar (Esc)"
                              data-testid={`btn-cancel-email-${k}`}
                            >
                              ✕
                            </Button>
                          </div>
                        ) : (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => startEditEmail(r)}
                                  className={`text-left text-sm hover:underline ${noEmail ? 'text-amber-700 italic' : 'text-blue-700'}`}
                                  data-testid={`btn-edit-email-${k}`}
                                >
                                  {r.email || '(click para agregar)'}
                                  {r.email && r.email_fuente === 'local' && (
                                    <span className="ml-1 text-[10px] text-amber-600" title="Guardado localmente (proveedor sin registro en el maestro)">(local)</span>
                                  )}
                                  {r.email && r.email_fuente === 'alegra' && (
                                    <span className="ml-1 text-[10px] text-gray-400" title="Heredado del email de Compras/Alegra — edítalo para fijar uno exclusivo de Estados de Cuenta">(heredado de Compras)</span>
                                  )}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>Click para editar — solo cambia el email de Estados de Cuenta (no toca el de Compras)</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-sm">{PESO(r.saldo_cxp)}</TableCell>
                      <TableCell className="text-right text-sm">{PESO(r.saldo_alegra)}</TableCell>
                      <TableCell className="text-xs text-gray-600">{ISO_TO_LOCAL(r.ultimo_envio_solicitud)}</TableCell>
                      <TableCell className="text-xs text-gray-600">{ISO_TO_LOCAL(r.ultimo_estado_cuenta)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {historial.length > 0 && (
            <div className="mt-6">
              <div className="flex items-center gap-2 mb-2 text-sm font-medium">
                <CalendarClock className="h-4 w-4" />
                Últimos envíos masivos
              </div>
              <div className="overflow-x-auto border rounded">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Trigger</TableHead>
                      <TableHead>Usuario</TableHead>
                      <TableHead className="text-right">Enviados</TableHead>
                      <TableHead className="text-right">Fallidos</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {historial.map((h) => (
                      <TableRow key={h.id}>
                        <TableCell className="text-xs">{ISO_TO_LOCAL(h.fecha)}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{h.trigger}</Badge></TableCell>
                        <TableCell className="text-xs">{h.usuario}</TableCell>
                        <TableCell className="text-right text-emerald-700">{h.enviados}</TableCell>
                        <TableCell className={`text-right ${h.fallidos > 0 ? 'text-rose-700' : 'text-gray-500'}`}>{h.fallidos}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Email Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Vista previa del correo</DialogTitle>
          </DialogHeader>
          <div className="border rounded p-4 bg-white text-sm space-y-3 font-sans">
            <p><strong>Asunto:</strong> Solicitud Estado de Cuenta — CHIRINO STEEL ING. Y CONT. SPA</p>
            <p><strong>De:</strong> concilia@chirinosteel.com</p>
            <hr />
            <p>Estimados <strong>[Nombre del Proveedor] (RUT [RUT del Proveedor])</strong>,</p>
            <p>Junto con saludar, les escribimos desde <strong>CHIRINO STEEL ING. Y CONT. SPA</strong> (RUT 76.738.421-1) para solicitar su <strong>Estado de Cuenta actualizado</strong>.</p>
            <p>Por favor, responda a este correo con la información (en PDF, Excel o en el cuerpo mismo del mensaje) a: <a className="text-blue-700">concilia@chirinosteel.com</a>.</p>
            <p>Una vez recibida la información, nuestro equipo de pagos podrá <strong>incluir las facturas pendientes en la próxima nómina de pago</strong>, de acuerdo a las condiciones comerciales acordadas.</p>
            <p>Quedamos atentos a su respuesta. Si necesita coordinar algún detalle, puede responder a este mismo correo.</p>
            <p><strong>Nota:</strong> Por favor, responda por separado este correo electrónico si somos clientes de dos o más empresas a su cargo.</p>
            <p>Saludos cordiales,<br /><strong>{config.firma || 'Concilia (Agente Pago Proveedores)'}</strong><br />CHIRINO STEEL ING. Y CONT. SPA<br />concilia@chirinosteel.com</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Config Auto Dialog */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Auto-envío mensual</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <label className="flex items-center gap-3 text-sm">
              <Switch
                checked={!!config.habilitado}
                onCheckedChange={(v) => setConfig({ ...config, habilitado: v })}
                data-testid="switch-auto"
              />
              <span>Habilitar envío automático mensual a TODOS los proveedores con email</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Día del mes (1-28)</Label>
                <Input
                  type="number" min={1} max={28}
                  value={config.dia}
                  onChange={(e) => setConfig({ ...config, dia: e.target.value })}
                  data-testid="input-dia"
                />
              </div>
              <div>
                <Label className="text-xs">Hora (0-23 UTC)</Label>
                <Input
                  type="number" min={0} max={23}
                  value={config.hora}
                  onChange={(e) => setConfig({ ...config, hora: e.target.value })}
                  data-testid="input-hora"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Firma</Label>
              <Input
                value={config.firma || ''}
                onChange={(e) => setConfig({ ...config, firma: e.target.value })}
                data-testid="input-firma"
              />
            </div>
            {config.ultimo_envio_auto && (
              <div className="text-xs text-gray-500">
                Último auto-envío: {ISO_TO_LOCAL(config.ultimo_envio_auto)}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfig(false)}>Cancelar</Button>
            <Button onClick={saveConfig} disabled={savingConfig} data-testid="btn-save-config">
              {savingConfig ? 'Guardando…' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
